import { createApp } from 'vue';
import App from './App.vue';
import './style.css';
// Shared chrome for the admin panel's seven panes. Every selector is
// namespaced under `.admin-main`, which only the panel renders.
import './admin/pane.css';
import { initGameAudio } from './game/audio';
// Side-effect import: registers the <altcha-widget> custom element exactly
// once, at the one place every phase of the app shares. `JoinScreen.vue` and
// `ResetPasswordScreen.vue` used to import this each on their own — never
// mounted together, so it should have been harmless, but a custom element's
// `customElements.define()` throws if it is ever asked to register the same
// tag name twice, and that is a real risk under Vite's dev-time module graph
// (two importers can each resolve their own copy of a side-effect import).
// Registering once here removes the question entirely.
import 'altcha';

/**
 * <altcha-widget>'s default English copy calls the checked state "Verified" —
 * too generic beside a page full of the game's own vocabulary, and there is no
 * per-instance string override in its `configuration` attribute (only
 * `Strings`-shaped text, and that interface is not exposed there at all). The
 * one documented seam is a global i18n store the package populates for `"en"`
 * the moment it is imported — `globalThis.$altcha.i18n.set("en", i18n)`, in its
 * own module-init code — so the override has to read that default back and
 * patch just the one key, or every other string (the label, the error text)
 * would be lost under a bare `{ verified: '...' }`. It inherits the page's own
 * font already (`.altcha *` inherits `font-family` from the host, which every
 * auth screen sets to `var(--body)`), so the replacement text needs nothing
 * extra to read in the game's own type.
 */
interface AltchaI18nStore {
  get: (key: string) => Record<string, string> | undefined;
  set: (key: string, value: Record<string, string>) => void;
}
const altchaGlobal = (window as unknown as { $altcha?: { i18n: AltchaI18nStore } }).$altcha;
if (altchaGlobal) {
  const en = altchaGlobal.i18n.get('en') ?? {};
  altchaGlobal.i18n.set('en', { ...en, verified: 'Verified with Altcha' });
}

// Audio starts listening here rather than in the arena, for two reasons: a
// browser will not start an AudioContext until the player has clicked
// *something*, and that first click is usually the sign-in button rather than
// anything in a match; and the garage's buttons want a click of their own. It
// builds no oscillators until there is something to play, and does nothing at
// all in a browser without Web Audio.
initGameAudio();

/**
 * `window.__sowGl()` — how many live WebGL contexts this tab is holding.
 *
 * The one failure in this codebase that is both silent and machine-dependent.
 * `CLAUDE.md` records why the catalogue renders stills: a browser caps WebGL
 * contexts and Chrome evicts past roughly sixteen, and an evicted context does
 * not throw — it simply stops drawing. The garage can hold three at once (the
 * hangar, the thumbnail rig while a card still is rendering, and the arena), so
 * "the hangar is disposed before the arena is built" is a real invariant with a
 * real failure behind it: a black arena, intermittently, on somebody else's
 * machine.
 *
 * It lives **here** rather than in either scene, because the question is about
 * the tab and the answer has to survive whichever scene is being torn down. A
 * probe published by `HangarScene` could not report on the moment after the
 * hangar has gone, which is precisely the moment worth measuring.
 *
 * `EngineStore.Instances` is Babylon's own registry — pushed on construction,
 * spliced on `dispose()` — so one entry is one context, and it counts engines
 * nobody kept a reference to. Dev only, and behind a dynamic import so that a
 * production bundle drops it and a development one does not put Babylon on the
 * critical path to the sign-in form.
 */
if (import.meta.env.DEV) {
  void import('@babylonjs/core/Engines/engineStore').then(({ EngineStore }) => {
    (window as unknown as Record<string, unknown>).__sowGl = () => ({
      contexts: EngineStore.Instances.length,
      names: EngineStore.Instances.map((engine) => engine.name || 'engine'),
    });
  });
}

/**
 * `window.__sowTouch(kind)` — force the input source, for the harness.
 *
 * Without it the touch build would have no automated coverage at all: the
 * harness runs headless Chromium on a desktop, `handheld()` correctly answers
 * false there, and a control scheme nothing exercises is the shape of thing that
 * silently rots — `RUNNING_GEAR_ENABLED` is the precedent this project keeps
 * citing, a kill switch with no test that noticed it stayed off for weeks.
 *
 * Read at arena construction, so it must be set **before** deploying; calling it
 * mid-battle does nothing, deliberately, because swapping the source under a
 * held control would leave that control down forever. Returns the answer that
 * would be given now, plus the fullscreen and orientation diagnostics, so a case
 * can assert what it actually got rather than what it asked for.
 *
 * Dev only, and dynamically imported so the probe cannot reach a production
 * bundle or put anything on the path to first paint.
 */
if (import.meta.env.DEV) {
  void Promise.all([import('./game/input-source'), import('./state/immersive')]).then(
    ([{ forceInputSource, wantsTouch, forcedInputSource }, { immersiveDiagnostics }]) => {
      (window as unknown as Record<string, unknown>).__sowTouch = (
        kind?: 'touch' | 'keyboard' | null,
      ) => {
        if (kind !== undefined) {
          forceInputSource(kind);
          /*
           * Anything that *reads* the source outside a deploy has to be told.
           *
           * `wantsTouch()` is a plain function and stays one — it sits in the
           * entry chunk, so a reactive wrapper would put machinery on the path
           * to the landing page's first paint. In real life it answers a fact
           * about the hardware that cannot change mid-session, so every reader
           * can simply re-read on some event it already listens for. This
           * override is the one thing that breaks that, and it exists only for
           * the harness, so the notification lives here beside it rather than in
           * `input-source.ts`.
           *
           * `RotatePrompt` is the first reader that needed it: without this a
           * case had to call `__sowTouch('touch')` *before* resizing to portrait
           * and would silently measure nothing in the other order — a trap in a
           * test helper, which is the worst place to leave one.
           */
          window.dispatchEvent(new Event('sow:input-source'));
        }
        return {
          forced: forcedInputSource(),
          wantsTouch: wantsTouch(),
          immersive: immersiveDiagnostics(),
        };
      };
    },
  );
}

createApp(App).mount('#app');
